import React, { useState } from "react";
import { GripVertical } from "lucide-react";

/**
 * Lightweight HTML5 drag-to-reorder list — zero dependencies.
 *
 * Usage:
 *   <SortableList items={items} onReorder={(reordered) => ...} keyOf={(x) => x.id}
 *                 renderItem={(item, dragHandle, index) => (
 *                   <div>{dragHandle}{item.name}</div>
 *                 )} />
 *
 * The renderItem function receives a pre-built drag handle JSX that the
 * consumer can place wherever it makes sense in the row.
 */
export function SortableList({ items, onReorder, keyOf, renderItem, className = "", testIdPrefix = "sortable" }) {
  const [dragIdx, setDragIdx] = useState(-1);
  const [overIdx, setOverIdx] = useState(-1);

  const move = (from, to) => {
    if (from === to || from < 0 || to < 0) return;
    const next = items.slice();
    const [moved] = next.splice(from, 1);
    next.splice(to, 0, moved);
    onReorder(next);
  };

  return (
    <div className={className} data-testid={`${testIdPrefix}-list`}>
      {items.map((item, i) => {
        const handle = (
          <button
            type="button"
            draggable
            onDragStart={(e) => {
              setDragIdx(i);
              e.dataTransfer.effectAllowed = "move";
              // Some browsers require setData for drag to start
              e.dataTransfer.setData("text/plain", String(i));
            }}
            onDragEnd={() => { setDragIdx(-1); setOverIdx(-1); }}
            className={`cursor-grab active:cursor-grabbing touch-none p-1.5 -m-1.5 rounded-lg transition-colors ${dragIdx === i ? "bg-brand-sky/20 text-brand-sky" : "text-neutral-400 hover:text-neutral-700 hover:bg-neutral-100"}`}
            aria-label="Drag to reorder"
            data-testid={`${testIdPrefix}-handle-${i}`}
          >
            <GripVertical className="w-4 h-4"/>
          </button>
        );
        return (
          <div
            key={keyOf(item)}
            onDragOver={(e) => { e.preventDefault(); setOverIdx(i); }}
            onDragLeave={() => setOverIdx((cur) => (cur === i ? -1 : cur))}
            onDrop={(e) => { e.preventDefault(); move(dragIdx, i); setOverIdx(-1); }}
            className={`relative transition-all ${dragIdx === i ? "opacity-50" : ""} ${overIdx === i && dragIdx !== i ? "scale-[1.01]" : ""}`}
            data-testid={`${testIdPrefix}-row-${i}`}
          >
            {/* Visual insert line when hovering between items */}
            {overIdx === i && dragIdx !== i && (
              <div className="absolute -top-1 left-0 right-0 h-1 bg-brand-sky rounded-full pointer-events-none"/>
            )}
            {renderItem(item, handle, i)}
          </div>
        );
      })}
    </div>
  );
}
